// src/supabase/auth.js
import { supabase } from './supabaseClient';
// import Router from 'vue-router';
import axios from 'axios';

// Sign up
export const signUp = async (email, password, isEligibleForTrial = false) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          eligible_for_trial: isEligibleForTrial,
        },
      },
    });

    if (error) throw error;

    // With the trigger we created, user_profiles will be automatically created
    // We can still update the eligible_for_trial field if needed
    if (data.user && isEligibleForTrial) {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ eligible_for_trial: true })
        .eq('id', data.user.id);

      if (updateError) console.error('Error updating user profile:', updateError);
    }

    // With ENABLE_EMAIL_AUTOCONFIRM=true, users are automatically confirmed
    return data;
  } catch (error) {
    console.error('Error during sign-up:', error);
    throw error;
  }
};

// Microsoft/Azure Sign In
export const signInWithMicrosoft = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'openid profile email',
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) throw error;

    // This initiates the OAuth flow and redirects the user
    return data;
  } catch (error) {
    console.error('🔐 [AUTH] ❌ Error during Microsoft Sign-In:', error);
    throw error;
  }
};

// Sign in
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    const user = data.user;
    // console.log(user);

    if (error) {
      // Check if the error is related to email verification
      if (error.message && (
          error.message.includes('Email not confirmed') ||
          error.message.includes('Email not verified') ||
          error.message.includes('not confirmed') ||
          error.message.includes('AuthApiError: Email not confirmed')
        )) {
        const verificationError = new Error('Email not verified');
        verificationError.code = 'EMAIL_NOT_VERIFIED';
        throw verificationError;
      }
      throw error;
    }



    // Delete user from LemList campaign
    await deleteUserFromLemListCampaign(email);

    // Return user for router to handle redirection
    return user;
  } catch (error) {
    console.error('Error during sign-in:', error);
    throw error;
  }
};

// Sign out
export const logout = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Redirect handled by the calling component
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

// Google Sign In
export const signInWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`
      }
    });

    if (error) throw error;

    // This initiates the OAuth flow and redirects the user
    // The response is handled by the router when the user is redirected back
    return data;
  } catch (error) {
    console.error('Error during Google Sign-In:', error);
    throw error;
  }
};

// Auth state change listener (equivalent to Firebase's onAuthStateChanged)
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null);
  });
};

// Function to resend verification email
export const resendVerificationEmail = async (email) => {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
    });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error resending verification email:', error);
    throw error;
  }
};

// Update document (for invitation tokens)
export const updateDocument = async (token) => {
  try {
    // First, check if the document exists
    const { data: invite, error: fetchError } = await supabase
      .from('invites')
      .select('*')
      .eq('token', token)
      .single();

    if (fetchError) throw fetchError;

    if (!invite) {
      throw new Error("Invite token does not exist or is invalid.");
    }

    // Mark invite as used
    const { error: updateError } = await supabase
      .from('invites')
      .update({ used: true })
      .eq('token', token);

    if (updateError) throw updateError;

  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

// Verify reset code not needed with Supabase as it's handled by the redirectTo URL

// Confirm password reset
export const confirmReset = async (newPassword) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error confirming password reset:', error);
    throw error;
  }
};

// LemList integration (keeping the same functionality)
const deleteUserFromLemListCampaign = async (userEmail) => {
  const campaignId = process.env.VUE_APP_LEMLIST_CAMPAIGN_ID;
  const apiKey = process.env.VUE_APP_LEMLIST_API_KEY;

  try {
    await axios.delete(
      `https://api.lemlist.com/campaigns/${campaignId}/leads/${userEmail}`,
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      }
    );
  } catch (error) {
    console.error('Error removing user from LemList campaign:', error.response ? error.response.data : error.message);
    // Not throwing to prevent blocking the sign-in process
  }
};

// Get current session
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  } catch (error) {
    console.error('Error getting session:', error);
    return null;
  }
};

// Get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Activer une session Supabase depuis les tokens retournés par le backend (login OTP)
export const setSessionFromTokens = async (accessToken, refreshToken) => {
  const { data, error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  // La verification du token aupres du serveur d'auth peut echouer alors que la
  // session vient d'etre ecrite localement (serveur lent, base qui repart). Dans
  // ce cas l'utilisateur est bel et bien connecte : lever une erreur lui
  // afficherait un echec sur une connexion valide.
  if (error && !(data && data.session)) throw error;
  return data && data.session ? data.session : null;
};
